import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  BarChart3, TrendingUp, Users, Send, ArrowUpRight, ArrowDownLeft,
  Target, DollarSign
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from "recharts";
import { Badge } from "../components/ui/badge";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Analytics = () => {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/analytics`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setAnalytics(response.data);
      } catch (error) {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [getAuthHeaders]);

  const COLORS = ['#0284C7', '#14B8A6', '#64748B', '#F59E0B'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 lg:pl-64">
        <Sidebar currentPath={location.pathname} />
        <main className="pt-20 lg:pt-8 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const referralData = [
    { name: 'Sent', value: analytics?.referrals?.sent || 0, color: '#0284C7' },
    { name: 'Received', value: analytics?.referrals?.received || 0, color: '#14B8A6' },
  ];

  const leadStatusData = [
    { name: 'Converted', value: analytics?.leads?.converted || 0 },
    { name: 'Pending', value: (analytics?.leads?.total || 0) - (analytics?.leads?.converted || 0) },
  ];

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="analytics-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Analytics
            </h1>
            <p className="text-slate-600">Track your referral performance and ROI</p>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Referrals Sent</span>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900">{analytics?.referrals?.sent || 0}</div>
              <p className="text-sm text-slate-500 mt-1">
                {analytics?.referrals?.sent_accepted || 0} accepted
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Referrals Received</span>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900">{analytics?.referrals?.received || 0}</div>
              <p className="text-sm text-slate-500 mt-1">
                {analytics?.referrals?.received_pending || 0} pending
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Leads</span>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900">{analytics?.leads?.total || 0}</div>
              <p className="text-sm text-slate-500 mt-1">
                {analytics?.leads?.converted || 0} converted
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Conversion Rate</span>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900">{analytics?.leads?.conversion_rate || 0}%</div>
              <p className="text-sm text-slate-500 mt-1">Lead to patient</p>
            </motion.div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Monthly Referrals Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Monthly Referral Trends</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.monthly || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="sent" name="Sent" fill="#0284C7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="received" name="Received" fill="#14B8A6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-primary"></div>
                  <span className="text-sm text-slate-600">Sent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500"></div>
                  <span className="text-sm text-slate-600">Received</span>
                </div>
              </div>
            </motion.div>

            {/* Referral Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Referral Distribution</h3>
              <div className="h-64 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={referralData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {referralData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                {referralData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-slate-600">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ROI Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gradient-to-br from-primary/5 to-emerald-50 rounded-xl border border-slate-200 p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">ROI Insights</h3>
                <p className="text-slate-600 text-sm">Understanding your referral value</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-slate-500 mb-1">Est. Patient Value</p>
                <p className="text-2xl font-bold text-slate-900">$2,500</p>
                <p className="text-xs text-slate-500">Average per referral</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-slate-500 mb-1">Referral Revenue</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${((analytics?.referrals?.received || 0) * 2500).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Estimated total value</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-slate-500 mb-1">Network Growth</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-primary">+{analytics?.referrals?.sent || 0}</p>
                  <Badge className="bg-primary/10 text-primary">Outbound</Badge>
                </div>
                <p className="text-xs text-slate-500">Referrals to network</p>
              </div>
            </div>

            <p className="text-sm text-slate-500 mt-4 text-center">
              💡 Tip: Physicians who send more referrals typically receive 40% more in return. Consider expanding your network.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
